import { OSC_CLICKID } from '@dx-ui/config-metrics';
import type { Link } from '@dx-ui/osc-link';
import type { LoginFormProps, LoginResponse } from '@dx-ui/osc-login';
import type { JoinModalProps } from '@dx-ui/osc-join-modal-button';
import type { LanguageSelector } from '@dx-ui/osc-language-selector';
import type {
  ComponentType,
  ComponentProps,
  ComponentPropsWithRef,
  MouseEventHandler,
  ReactNode,
  RefObject,
} from 'react';
import { Surfaces } from '@dx-ui/global-ui-prism';
import type { HEADER_VARIANT_TYPE } from './header.constants';
import type { IconNames } from '../../../../osc/icon/src/lib/osc-icon-map';

export type GlobalNavVariantData = {
  shouldShowGlobalNav: boolean;
  isHamBurgerMenuNavVariant: boolean;
  isExploreHiltonNavVariant: boolean;
};

export type HeaderVariantType = ValuesOf<typeof HEADER_VARIANT_TYPE>;

export const HEADER_CLICK_IDS = {
  FindStay: 'gh_findstay',
  HotelsResorts: 'gh_hotelsresorts',
  MemberName: 'gh_membername',
  MemberNameSignOut: 'gh_membername_signout',
  MemberNameHiltonHonorsHome: 'gh_membername_hiltonhonorshome',
  MemberNameActivity: 'gh_membername_activity',
  MemberNamePoints: 'gh_membername_points',
  MemberNameProfile: 'gh_membername_profile',
  /** Legacy account click ids. Keep these strings so existing trackNavClick beacons stay unchanged. */
  MemberNameHiltonHonorsActivity: 'gh_membername_hiltonhonorsactivity',
  MemberNameHiltonHonorsPoints: 'gh_membername_hiltonhonorspoints',
  MemberNameHiltonHonorsProfile: 'gh_membername_hiltonhonorsprofile',
  MemberNameHiltonHonorsYourOffers: 'gh_membername_hiltonhonorsyouroffers',
  MemberNameGoHiltonProgramDetails: 'gh_membername_gohiltonprogramdetails',
  MemberNameManageFriendsAndFamily: 'gh_membername_managefriendsandfamily',
  MemberNameHgvMaxRateDetails: 'gh_membername_hgvmaxratedetails',
  MemberNameManageHiltonForBusiness: 'gh_membername_managehiltonforbusiness',
  Join: OSC_CLICKID.JOIN,
  SignIn: OSC_CLICKID.SIGNIN,
} as const;

export type HeaderClickIDs = (typeof HEADER_CLICK_IDS)[keyof typeof HEADER_CLICK_IDS];

export type GlobalNavigationClick = {
  clickId: string;
};

export type LinkWithExperimentationConfiguration = Link & {
  experimentationConfiguration?: CmsExperimentationConfiguration;
  isLoggedIn?: boolean;
};

type DrawerBaseProps = {
  label: string;
  onClick?: () => void;
};

export type TDrawerLink = DrawerBaseProps & {
  link: LinkWithExperimentationConfiguration;
};

export type TDrawerItem = DrawerBaseProps & {
  link?: LinkWithExperimentationConfiguration;
  subMenu?: TDrawerLink[];
  label_noTx?: string;
};

export type TDrawerList = {
  menuItems?: TDrawerItem[];
  isOpen: boolean;
  onClose: () => void;
  globalNavLinks?: GlobalNavLink[];
  surfaceType?: Surfaces;
  suppressLogo?: boolean;
  brand: {
    url: string;
    name: string;
    code: string;
    target?: ComponentProps<'a'>['target'];
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  };
  heading?: string;
  languageSelectorOptions?: ComponentProps<typeof LanguageSelector>;
  headerVariantType?: HeaderVariantType;
  onGlobalNavigationClick?: HeaderProps['onGlobalNavigationClick'];
  isLegacyGlobalNavigationClickAnalyticsEnabled?: HeaderProps['isLegacyGlobalNavigationClickAnalyticsEnabled'];
};

export type TBrandHeaderBody = Pick<
  HeaderProps,
  | 'brand'
  | 'onSignOut'
  | 'onSignInAttempt'
  | 'user'
  | 'userLinks'
  | 'loginOptions'
  | 'languageSelectorOptions'
  | 'isFluid'
  | 'suppressLogo'
  | 'shouldSuppressHonorsAccountLinks'
  | 'heading'
  | 'shouldSuppressFindStay'
  | 'shouldSuppressJoinAndSignIn'
  | 'shouldOpenAccountLinksInNewWindow'
  | 'shouldOpenJoinInNewWindow'
  | 'shouldOpenJoinInPage'
  | 'mvts'
  | 'surfaceType'
  | 'headerVariantType'
  | 'globalNavLinks'
  | 'isTranslucent'
  | 'onGlobalNavigationClick'
  | 'isLegacyGlobalNavigationClickAnalyticsEnabled'
> & {
  menuItems?: TDrawerItem[];
  surfaceClassName?: string;
  wrapperId: Required<HeaderProps>['wrapperId'];
  headerVariantType?: HeaderVariantType;
  analytics?: HeaderAnalytics;
};

export type UserLinks = {
  additionalLinks?: Link[];
  signInLink?: Link;
  signUpLink?: Link;
  signUpComponent?: ComponentType<{
    children: ReactNode;
    className: string;
    loggedInButtonRef: RefObject<HTMLButtonElement | null>;
  }>;
  accountLink?: Omit<Link, 'label'>;
  honorsActivityLink?: Omit<Link, 'label'>;
  honorsPointsLink?: Omit<Link, 'label'>;
  honorsProfileLink?: Omit<Link, 'label'>;
  yourOffersLink?: Omit<Link, 'label'>;
  friendsAndFamilyLink?: Omit<Link, 'label'>;
  hgvMaxLink?: Omit<Link, 'label'>;
  b2bMemberLink?: Link;
  goHiltonLink?: Omit<Link, 'label'>;
  findStayLink?: Omit<Link, 'label'>;
};

export type Mvts = {
  [key: string]: {
    variant: string;
  };
};

/** DS theme tokens used to compute a logo surface from luminance */
export type LogoThemeColors = { text?: string; background?: string };

/** Image variant rendered by HeaderLogo/HeaderLogoLink. `default` is the unmodified brand mark. */
export type HeaderLogoVariant = 'black' | 'inverted' | 'default';

/** Semantic login and join callbacks Header forwards to its child mounts. */
export type HeaderAnalytics = NonNullable<LoginFormProps['analytics']> &
  NonNullable<JoinModalProps['analytics']> &
  Pick<LoginFormProps, 'isLegacySignInAnalyticsEnabled'>;

export type HeaderProps = ComponentPropsWithRef<'header'> & {
  /** Brand url information */
  brand: {
    url: string;
    name: string;
    code: string;
    target?: ComponentProps<'a'>['target'];
    onClick?: MouseEventHandler<HTMLAnchorElement>;
  };
  /** Flag to control fluidity. @default true */
  isFluid?: boolean;
  /** Navigation links for the header */
  mainNavLinks?: LinkWithExperimentationConfiguration[];
  /** Links used for the mega navigation */
  megaNavLinks?: TDrawerItem[];
  globalNavLinks?: TDrawerItem[];
  /** Reports a global navigation selection without coupling the header to an analytics transport. */
  onGlobalNavigationClick?: (payload: GlobalNavigationClick) => void;
  /**
   * When true (default), global nav clicks use legacy `trackNavClick`.
   * When false, `onGlobalNavigationClick` receives the click id instead.
   */
  isLegacyGlobalNavigationClickAnalyticsEnabled?: boolean;
  /** Custom className for global nav theming */
  globalNavCustomClassName?: string;
  /** Custom surface className applied to the desktop bottom nav link bar (osc-nav-bottom-link-area), enabling per-brand theming */
  bottomNavLinkAreaClassName?: string;
  /** Explicit surface type for the logo when it lives in the bottom nav bar, overriding the default logoSurfaceType */
  navHeaderLinksLogoSurfaceType?: Surfaces;
  /** DS theme tokens for the bottom nav surface; the lib computes the logo surface from luminance when supplied */
  navHeaderLinksLogoColors?: LogoThemeColors;
  /** Luminance-computed surface type for the Hilton WW logo inside the global nav overlay */
  globalNavLogoSurfaceType?: Surfaces;
  /** DS theme tokens for the global nav surface; the lib computes the logo surface from luminance when supplied */
  globalNavLogoColors?: LogoThemeColors;
  /** Forces the global nav / mobile drawer logo variant, bypassing the surface/color computation entirely */
  globalNavLogoVariant?: HeaderLogoVariant;
  /** Forces the desktop bottom nav logo variant, bypassing the surface/color computation entirely */
  navHeaderLinksLogoVariant?: HeaderLogoVariant;
  /** Sign out callback */
  onSignOut: () => Promise<void>;
  /** Sign in callback */
  onSignInAttempt: (response: LoginResponse) => Promise<void>;
  /** Flag to show new header. @default true */
  showNewHeader?: boolean;
  /** Flag to suppress the logo. @default false */
  suppressLogo?: boolean;
  /** Flag to suppress the honors activity, points, and profile links in the account menu */
  shouldSuppressHonorsAccountLinks?: boolean;
  /** Flag to suppress the find stay link */
  shouldSuppressFindStay?: boolean;
  /** Flag to suppress the Join and Sign In buttons */
  shouldSuppressJoinAndSignIn?: boolean;
  /** Flag to suppress the B2B member link */
  shouldSuppressB2BLink?: boolean;
  /** Flag to suppress the Go Hilton (TMTP) link */
  shouldSuppressGoHiltonLink?: boolean;
  /** Flag to suppress the HGV Max link */
  shouldSuppressHGVMaxLink?: boolean;
  /** Flag to suppress the Friends and Family link */
  shouldSuppressFriendsAndFamilyLink?: boolean;
  /** Flag to open find stay and account links in new window. @default true */
  shouldOpenAccountLinksInNewWindow?: boolean;
  /** Flag to open join hilton honors link in new window when `shouldOpenJoinInPage` is `true` */
  shouldOpenJoinInNewWindow?: boolean;
  /** Flag to open join page instead of the join modal */
  shouldOpenJoinInPage?: boolean;
  /** Content to be rendered inside a H1 which wraps the logo link */
  heading?: string;
  /** User information */
  user?: {
    name: string;
    honorsTier: string;
    honorsPoints: number;
    honorsPointsFmt: string;
    hhonorsNumber?: string;
  };
  userLinks?: UserLinks;
  loginOptions?: Pick<LoginFormProps, 'loginFormConfig'>;
  languageSelectorOptions?: ComponentProps<typeof LanguageSelector>;
  /** Id of an element that wraps your app. Required for selecting items to aria-hidden when nav windows are open.
   * This is to combat virtual cursor in some screen readers focusing elements behind the nav.
   * @default '__next'
   */
  wrapperId?: string;
  /**
   * Can be used to pass MVTs info for header experiments, for example { '5758': { variant: "B" } }
   */
  mvts?: Mvts;
  /** Surface type for the header. @default 'base' */
  surfaceType?: Surfaces;
  headerVariantType?: HeaderVariantType;
  isTranslucent?: boolean;
  isReimagineThemeExperience?: boolean;
  analytics?: HeaderAnalytics;
};

/**
 * Props for the new HeaderWrapper component and composable header variants.
 * Extends the legacy HeaderProps with additional feature flags for controlling user link visibility.
 */
export type HeaderWrapperProps = HeaderProps & {
  /** Global navigation links */
  globalNavLinks?: TDrawerItem[];
  /** Flag to suppress the B2B member link */
  shouldSuppressB2BLink?: boolean;
  /** Flag to suppress the Go Hilton (TMTP) link */
  shouldSuppressGoHiltonLink?: boolean;
  /** Flag to suppress the HGV Max link */
  shouldSuppressHGVMaxLink?: boolean;
  /** Flag to suppress the Friends and Family link */
  shouldSuppressFriendsAndFamilyLink?: boolean;
  isTranslucent?: boolean;
};

export type MegaMenuProps = Pick<
  HeaderProps,
  | 'isFluid'
  | 'suppressLogo'
  | 'shouldSuppressHonorsAccountLinks'
  | 'brand'
  | 'shouldSuppressFindStay'
  | 'shouldSuppressJoinAndSignIn'
  | 'shouldOpenAccountLinksInNewWindow'
  | 'shouldOpenJoinInNewWindow'
  | 'shouldOpenJoinInPage'
  | 'user'
  | 'languageSelectorOptions'
  | 'userLinks'
  | 'loginOptions'
  | 'onSignInAttempt'
  | 'onSignOut'
  | 'heading'
  | 'mvts'
  | 'surfaceType'
  | 'headerVariantType'
  | 'globalNavLinks'
  | 'isTranslucent'
  | 'onGlobalNavigationClick'
  | 'isLegacyGlobalNavigationClickAnalyticsEnabled'
> & {
  mainNavLinks?: TDrawerItem[];
  isHamburgerNavVariant?: boolean;
  analytics?: HeaderAnalytics;
};

export type GlobalNavLink = TDrawerItem;

export type GlobalNavMenuProps = {
  menuItems?: Array<{
    label: string;
    url?: string;
    subMenu?: Array<{
      label: string;
      url?: string;
    }>;
  }>;
  globalNavLinks?: GlobalNavLink[];
  surfaceType?: Surfaces;
  isTranslucent?: boolean;
  className?: string;
  customClassName?: string;
  /** Luminance-computed surface type for the Hilton WW logo, overrides the static surfaceType check */
  globalNavLogoSurfaceType?: Surfaces;
} & { headerVariantType?: TBrandHeaderBody['headerVariantType'] };

export type IconName = (typeof IconNames)[number];

export type NavItemSection = 'navigation' | 'user';

export type NavItemProps = Omit<React.ComponentProps<'li'>, 'className' | 'children'> & {
  isOpen?: boolean;
  children: React.ReactElement<HTMLElement>;
  className?: string;
};
